import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Button } from "@material-ui/core";
import fieldStyles from "../account.module.css";
import modalStyles from "./changePassword.module.css";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import useAxiosPrivate from "../../../utils/hooks/useAxiosPrivate";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useSelector } from "react-redux";
import Alert from "../../../components/UI/alert";
import ModalCloseButton from "../../../components/UI/ModalCloseButton";
import {
    centeredModalBoxSx,
    modalBackdropProps,
} from "../../../utils/styles/modal.overlay";

const style = centeredModalBoxSx({
    width: 700,
    maxWidth: "calc(100vw - 48px)",
    overflow: "visible",
    p: 0,
});

const inputClasses = {
    root: `${fieldStyles.loginFieldInputRoot} ${modalStyles.passwordInputRoot}`,
};

const hiddenLabelClasses = {
    root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
};

let ChangePasswordSchema = Yup.object().shape({
    currentPassword: Yup.string()
        .min(8, "Password has to be 8 or more characters with a mix of letters & numbers")
        .required(
            "Password has to be 8 or more characters with a mix of letters & numbers"
        ),
    newPassword: Yup.string()
        .min(8, "Password has to be 8 or more characters with a mix of letters & numbers")
        .required(
            "Password has to be 8 or more characters with a mix of letters & numbers"
        ),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords don't match")
        .required(
            "Password has to be 8 or more characters with a mix of letters & numbers"
        ),
});

const PasswordVisibilityToggle = ({ visible, onToggle }) => (
    <InputAdornment position="end">
        <IconButton
            aria-label="toggle password visibility"
            onClick={onToggle}
            onMouseDown={(event) => event.preventDefault()}
            className={modalStyles.visibilityButton}
            edge="end"
        >
            {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </IconButton>
    </InputAdornment>
);

export default function AccountModal(props) {
    const axios = useAxiosPrivate();
    const { setOpen, open, setOpenedAccount, setSuccess } = props;
    const email = useSelector((state) => state.auth.email);
    const initialValues = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    };
    const [alert, setAlert] = useState({
        showAlert: false,
        severity: "success",
        message: "",
    });

    useEffect(() => {
        setOpenedAccount(false);
        // eslint-disable-next-line
    }, [open]);

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const submit = async (e) => {
        try {
            await axios.post("/user/change_password", {
                email: email,
                password: e.currentPassword,
                new_password: e.newPassword,
            });
            setSuccess(true);
        } catch (error) {
            if (error.response && error.response?.data) {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: error.response?.data?.message,
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: error.response?.data?.message,
                    });
                }, 5000);
            } else {
                setAlert({
                    showAlert: true,
                    severity: "error",
                    message: "Something was wrong!",
                });
                setTimeout(() => {
                    setAlert({
                        showAlert: false,
                        severity: "error",
                        message: "Something was wrong!",
                    });
                }, 5000);
            }
        }
    };

    return (
        <div>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                BackdropProps={modalBackdropProps}
            >
                <Box sx={style}>
                    <div className={modalStyles.modalContent}>
                        <div className={modalStyles.closeRow}>
                            <ModalCloseButton
                                style={{ position: "static" }}
                                onClick={() => setOpen(false)}
                            />
                        </div>
                        <Formik
                            initialValues={initialValues}
                            onSubmit={submit}
                            validationSchema={ChangePasswordSchema}
                        >
                            {({ dirty, isValid }) => (
                                <Form className={modalStyles.form}>
                                    <div className={modalStyles.fieldBlock}>
                                        <label
                                            className={modalStyles.fieldLabel}
                                            htmlFor="change-password-current"
                                        >
                                            Current Password
                                        </label>
                                        <Field
                                            id="change-password-current"
                                            name="currentPassword"
                                            label="Current Password"
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            margin="none"
                                            type={showCurrentPassword ? "text" : "password"}
                                            className={modalStyles.passwordTextField}
                                            InputLabelProps={{ classes: hiddenLabelClasses }}
                                            InputProps={{
                                                classes: inputClasses,
                                                notched: false,
                                                endAdornment: (
                                                    <PasswordVisibilityToggle
                                                        visible={showCurrentPassword}
                                                        onToggle={() =>
                                                            setShowCurrentPassword((prev) => !prev)
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>

                                    <div className={modalStyles.fieldBlock}>
                                        <label
                                            className={modalStyles.fieldLabel}
                                            htmlFor="change-password-new"
                                        >
                                            New Password
                                        </label>
                                        <Field
                                            id="change-password-new"
                                            name="newPassword"
                                            label="New Password"
                                            placeholder="Enter"
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            margin="none"
                                            type={showNewPassword ? "text" : "password"}
                                            className={modalStyles.passwordTextField}
                                            InputLabelProps={{ classes: hiddenLabelClasses }}
                                            InputProps={{
                                                classes: inputClasses,
                                                notched: false,
                                                endAdornment: (
                                                    <PasswordVisibilityToggle
                                                        visible={showNewPassword}
                                                        onToggle={() =>
                                                            setShowNewPassword((prev) => !prev)
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                        <p className={modalStyles.passwordHint}>
                                            Use 8 or more characters with a mix of letters & numbers
                                        </p>
                                    </div>

                                    <div className={modalStyles.fieldBlock}>
                                        <Field
                                            id="change-password-confirm"
                                            name="confirmPassword"
                                            label="Confirm Password"
                                            placeholder="Re-Enter"
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            margin="none"
                                            type={showConfirmPassword ? "text" : "password"}
                                            className={modalStyles.passwordTextField}
                                            InputLabelProps={{ classes: hiddenLabelClasses }}
                                            InputProps={{
                                                classes: inputClasses,
                                                notched: false,
                                                endAdornment: (
                                                    <PasswordVisibilityToggle
                                                        visible={showConfirmPassword}
                                                        onToggle={() =>
                                                            setShowConfirmPassword((prev) => !prev)
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    </div>

                                    <div className={modalStyles.saveRow}>
                                        {dirty && isValid ? (
                                            <Button
                                                type="submit"
                                                disableRipple
                                                disableFocusRipple
                                                className={`${fieldStyles.nameActionButton} ${fieldStyles.saveButton}`}
                                            >
                                                Save
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                disabled
                                                disableRipple
                                                disableFocusRipple
                                                className={`${fieldStyles.nameActionButton} ${fieldStyles.saveButtonDisabled}`}
                                            >
                                                Save
                                            </Button>
                                        )}
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                    <Alert
                        showAlert={alert.showAlert}
                        severity={alert.severity}
                        message={alert.message}
                        setAlert={setAlert}
                    />
                </Box>
            </Modal>
        </div>
    );
}
